import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/authSlice';
import { useAppSettings } from '../hooks/useAppSettings';
import api from '../services/api';

// Full admin nav — menu_code must match menus table
const ALL_NAV_GROUPS = [
  { label: 'Main', items: [
    { path: '/admin/dashboard',    icon: '🏠', label: 'Dashboard',       code: 'dashboard' },
    { path: '/admin/applications', icon: '📂', label: 'All Applications', code: 'applications' },
  ]},
  { label: 'Masters', items: [
    { path: '/admin/masters/noc',             icon: '📜', label: 'NOC Types',        code: 'masters_noc' },
    { path: '/admin/masters/department',      icon: '🏢', label: 'Departments',      code: 'masters_dept' },
    { path: '/admin/masters/designation',     icon: '👤', label: 'Designations',     code: 'masters_desig' },
    { path: '/admin/masters/employee',        icon: '👷', label: 'Employees',        code: 'masters_emp' },
    { path: '/admin/masters/users',           icon: '👥', label: 'Citizens',         code: 'masters_users' },
    { path: '/admin/masters/document-demand', icon: '📁', label: 'Document Demands', code: 'masters_docs' },
    { path: '/admin/masters/notice',          icon: '📢', label: 'Notices',          code: 'masters_notice' },
  ]},
  { label: 'RBAC', items: [
    { path: '/admin/rbac/roles',            icon: '🔒', label: 'Roles',            code: 'rbac_roles' },
    { path: '/admin/rbac/menu-permissions', icon: '🎛️', label: 'Menu Permissions', code: 'rbac_permissions' },
    { path: '/admin/operator-manager',      icon: '👷', label: 'Operator Manager', code: 'operator_manager' },
  ]},
  { label: 'Workflow', items: [
    { path: '/admin/workflow-routing',   icon: '⚙️', label: 'Workflow Routing',   code: 'workflow' },
    { path: '/admin/sarai-zone-routing', icon: '🏘️', label: 'Sarai Zone Routing', code: 'sarai_zone' },
    { path: '/admin/flow-tracking',      icon: '🔀', label: 'Flow Tracking',      code: 'flow_tracking' },
  ]},
  { label: 'Analytics', items: [
    { path: '/admin/reports',        icon: '📊', label: 'Reports',        code: 'reports' },
    { path: '/admin/noc-report',     icon: '📋', label: 'NOC Report',     code: 'reports' },
    { path: '/admin/dept-report',    icon: '🏢', label: 'Dept Report',    code: 'reports' },
    { path: '/admin/dept-noc-report', icon: '📊', label: 'Dept NOC Report', code: 'reports' },
    { path: '/admin/feedbacks',      icon: '📝', label: 'Feedbacks',      code: 'feedbacks' },
    { path: '/admin/pending-report', icon: '⏰', label: 'Pending Report', code: 'pending_report' },
    { path: '/admin/activity-logs',  icon: '📋', label: 'Activity Logs',  code: 'activity_logs' },
  ]},
  { label: 'Account', items: [
    { path: '/admin/change-password', icon: '🔒', label: 'Change Password', code: 'change_password' },
  ]},
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [permMap, setPermMap]           = useState(null); // null = loading, {} = no perms set
  const { user, role } = useSelector(s => s.auth);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const settings  = useAppSettings();
  const handleLogout = () => { dispatch(logout()); navigate('/login'); };

  const headerColor = settings?.header_color || '#312e81';
  const isHead      = role === 'head' || role === 'HD';
  const isFullAccess = ['admin', 'developer'].includes(role);
  const roleLabel   = isHead ? 'Head' : 'Admin';

  // Load permissions for non-admin roles
  useEffect(() => {
    if (isFullAccess) { setPermMap({}); return; }
    api.get('/rbac/my-permissions')
      .then(r => setPermMap(r.permissions || {}))
      .catch(() => setPermMap({}));
  }, [role]);

  // Filter nav groups based on permissions
  const navGroups = ALL_NAV_GROUPS
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        if (isFullAccess) return true;
        if (!permMap) return false;
        // If no permissions set at all for this role — show nothing (wait for load)
        return permMap[item.code]?.can_view === 1;
      })
    }))
    .filter(group => group.items.length > 0);

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: settings?.bg_color || '#f7fafc' }}>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} overflow-y-auto`}
        style={{ background: `linear-gradient(180deg, ${headerColor} 0%, ${headerColor}cc 100%)` }}>
        <div className="flex flex-col min-h-full">

          {/* Logo */}
          <div className="px-6 py-5 border-b border-white/10 sticky top-0 z-10" style={{ background: headerColor }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-xl">🏛️</div>
              <div>
                <p className="text-white font-bold text-sm truncate max-w-[140px]">
                  {settings?.app_name || 'Gov NOC Portal'}
                </p>
                <p className="text-white/70 text-xs">{roleLabel} Panel</p>
              </div>
            </div>
          </div>

          {/* User info */}
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-xs text-white/60">{roleLabel}</p>
            <p className="text-white font-medium text-sm truncate">{user?.name}</p>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-4">
            {permMap === null ? (
              <p className="text-white/50 text-xs text-center py-4">Loading...</p>
            ) : navGroups.map(group => (
              <div key={group.label}>
                <p className="text-white/50 text-xs font-semibold uppercase tracking-wider px-2 mb-1">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map(item => (
                    <NavLink key={item.path} to={item.path}
                      className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                      <span>{item.icon}</span><span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-3 border-t border-white/10">
            <button onClick={handleLogout} className="sidebar-link w-full text-left">
              <span>🚪</span><span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4 shadow-sm">
          <button className="lg:hidden text-gray-500 text-xl" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <h2 className="text-gray-700 font-semibold text-sm flex-1">
            {settings?.app_name || 'Government NOC Management System'}
          </h2>
          <span className={`text-xs px-3 py-1 rounded-full border ${
            isHead ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-purple-50 border-purple-200 text-purple-700'
          }`}>{roleLabel}</span>

          {/* User Dropdown */}
          <div className="relative">
            <button onClick={() => setDropdownOpen(p => !p)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded-xl hover:bg-gray-100 transition-all">
              <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-xs">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <span className="hidden sm:block max-w-[120px] truncate font-medium">{user?.name}</span>
              <span className="text-gray-400 text-xs">▾</span>
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-800 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-400">{roleLabel}</p>
                </div>
                <Link to="/admin/change-password"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-all">
                  🔒 Change Password
                </Link>
                <button onClick={() => { setDropdownOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-all">
                  🚪 Logout
                </button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-6"><Outlet /></main>
      </div>
    </div>
  );
}